import { useEffect, useState } from "react";
import { X, Cable, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const MODE_LABELS = {
  A: "Koniec A (Mufa A)",
  B: "Koniec B (Mufa B)",
  pass: "Przelotowy (WE/WY)",
  none: "Nie dołączaj",
};

const MODE_COLOR = { A: "#34d399", B: "#fbbf24", pass: "#22d3ee" };

function CableMiniMap({ rows, cLat, cLng, t }) {
  const geos = rows.filter((r) => (r.geometry || []).length >= 2 && r.checked);
  if (cLat == null || cLng == null || !geos.length) return null;
  const cos = Math.cos((cLat * Math.PI) / 180);
  const proj = (pt) => ({ x: (pt[1] - cLng) * cos, y: -(pt[0] - cLat) });
  const pts = [{ x: 0, y: 0 }];
  geos.forEach((r) => r.geometry.forEach((p) => pts.push(proj(p))));
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const W = 320, H = 150, PAD = 18;
  const sx = (maxX - minX) || 1e-9, sy = (maxY - minY) || 1e-9;
  const scale = Math.min((W - 2 * PAD) / sx, (H - 2 * PAD) / sy);
  const tx = (p) => PAD + (p.x - minX) * scale + (W - 2 * PAD - sx * scale) / 2;
  const ty = (p) => PAD + (p.y - minY) * scale + (H - 2 * PAD - sy * scale) / 2;
  const cx = tx({ x: 0, y: 0 }), cy = ty({ x: 0, y: 0 });
  return (
    <div className="mb-3 rounded border border-slate-700 bg-slate-900/60 p-2" data-testid="attach-minimap">
      <div className="text-[10px] text-slate-400 mb-1">{t("Podgląd: przebieg kabli względem mufy")}</div>
      <svg width={W} height={H} className="w-full">
        {geos.map((r) => {
          const path = r.geometry.map((p) => { const q = proj(p); return `${tx(q)},${ty(q)}`; }).join(" ");
          const col = MODE_COLOR[r.mode] || "#64748b";
          const mid = r.geometry[Math.floor(r.geometry.length / 2)]; const mq = proj(mid);
          return (
            <g key={r.cable_id}>
              <polyline points={path} fill="none" stroke={col} strokeWidth="2.5" strokeLinejoin="round" />
              <text x={tx(mq)} y={ty(mq) - 4} fontSize="9" fill={col} textAnchor="middle">{r.cable_number}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r="6" fill="#a855f7" stroke="#fff" strokeWidth="1.5" />
        <text x={cx} y={cy - 9} fontSize="9" fill="#e9d5ff" textAnchor="middle" fontWeight="700">Mufa</text>
      </svg>
      <div className="flex gap-3 mt-1 text-[9px] text-slate-400">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-1 inline-block" style={{ background: MODE_COLOR.pass }} />{t("Przelotowy")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-1 inline-block" style={{ background: MODE_COLOR.A }} />{t("Koniec A")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-1 inline-block" style={{ background: MODE_COLOR.B }} />{t("Koniec B")}</span>
      </div>
    </div>
  );
}

export default function AttachCablesDialog({ closureId, closureNumber, initialCandidates, closureLat, closureLng, onClose, onDone }) {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [cLat, setCLat] = useState(closureLat ?? null);
  const [cLng, setCLng] = useState(closureLng ?? null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const init = (cands) => setRows((cands || []).map((c) => ({
      ...c, checked: true, mode: c.current_mode || c.suggested_mode || "pass",
    })));
    if (initialCandidates) { init(initialCandidates); return; }
    api.get(`/closures/${closureId}/cable-candidates`)
      .then((r) => { init(r.data?.candidates || []); if (r.data?.closure_lat != null) { setCLat(r.data.closure_lat); setCLng(r.data.closure_lng); } })
      .catch(() => setRows([]));
  }, [closureId, initialCandidates]);

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.cable_id === id ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const items = rows.map((r) => ({ cable_id: r.cable_id, mode: r.checked ? r.mode : "none" }));
      const { data } = await api.post(`/closures/${closureId}/attach-cables`, { items });
      toast.success(`${t("Zaktualizowano podłączenie kabli")}: ${data.applied}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-4" data-testid="attach-cables-dialog">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-base flex items-center gap-2">
            <Cable size={16} className="text-cyan-400" /> {t("Dołącz kable do mufy")} {closureNumber}
          </h3>
          <button onClick={onClose} data-testid="attach-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-4">
          {rows === null ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center"><Loader2 size={16} className="animate-spin" /> {t("Ładowanie…")}</div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="attach-empty">{t("Brak kabli w pobliżu tej mufy (promień 3 m).")}</p>
          ) : (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-400 mb-1">{t("Zaznacz kable do dołączenia i wybierz sposób podłączenia dla każdego z nich.")}</p>
              <CableMiniMap rows={rows} cLat={cLat} cLng={cLng} t={t} />
              {rows.map((r) => (
                <div key={r.cable_id} className="flex items-center gap-3 rounded border border-slate-700 bg-slate-900/50 px-3 py-2" data-testid={`attach-row-${r.cable_id}`}>
                  <input type="checkbox" checked={r.checked} onChange={(e) => setRow(r.cable_id, { checked: e.target.checked })}
                    data-testid={`attach-check-${r.cable_id}`} className="w-4 h-4 accent-cyan-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-white truncate">{r.cable_number}</div>
                    <div className="text-[11px] text-slate-500">{r.fiber_count} wł.{r.cable_type ? ` · ${r.cable_type}` : ""} · {r.distance_m} m</div>
                  </div>
                  <select value={r.mode} disabled={!r.checked} onChange={(e) => setRow(r.cable_id, { mode: e.target.value })}
                    data-testid={`attach-mode-${r.cable_id}`}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-40 shrink-0">
                    <option value="A" disabled={r.a_taken}>{t(MODE_LABELS.A)}{r.a_taken ? " — zajęty" : ""}</option>
                    <option value="B" disabled={r.b_taken}>{t(MODE_LABELS.B)}{r.b_taken ? " — zajęty" : ""}</option>
                    <option value="pass">{t(MODE_LABELS.pass)}</option>
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-700">
          <button onClick={onClose} data-testid="attach-cancel" className="px-3 py-1.5 rounded border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm">{t("Anuluj")}</button>
          <button onClick={save} disabled={busy || !rows || !rows.length} data-testid="attach-save"
            className="px-4 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-sm font-medium">
            {busy ? t("Zapisywanie…") : t("Zapisz podłączenie")}
          </button>
        </div>
      </div>
    </div>
  );
}
